import React, { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  TextField,
  Button,
  Alert,
  CircularProgress,
  FormControl,
  InputLabel,
  Select,
  MenuItem
} from '@mui/material';
import { platformApiFetch } from './platformApi';
import { usePlatformAuth } from '../context/PlatformAuthContext';

const STATUSES = ['active', 'inactive', 'archived'];

export default function PlatformPermissionFormPage() {
  const { id } = useParams();
  const isEdit = Boolean(id);
  const navigate = useNavigate();
  const { user, ready } = usePlatformAuth();

  const [form, setForm] = useState({
    permission_code: '',
    permission_name: '',
    module_name: '',
    action_name: '',
    description: '',
    status: 'active'
  });
  const [isSystem, setIsSystem] = useState(false);
  const [loading, setLoading] = useState(isEdit);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (ready && user?.role !== 'super_admin') {
      navigate('/platform/permissions', { replace: true });
    }
  }, [ready, user, navigate]);

  const load = useCallback(async () => {
    if (!isEdit) return;
    setLoading(true);
    setError(null);
    try {
      const res = await platformApiFetch(`/api/platform/permissions/${id}`);
      const json = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(json.error || 'Failed to load');
      const p = json.permission;
      setIsSystem(Boolean(p.is_system_permission || p.is_system));
      setForm({
        permission_code: p.permission_code || '',
        permission_name: p.permission_name || '',
        module_name: p.module_name || '',
        action_name: p.action_name || '',
        description: p.description || '',
        status: p.status || 'active'
      });
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [isEdit, id]);

  useEffect(() => {
    load();
  }, [load]);

  const submit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      if (isEdit) {
        const body = {
          permission_name: form.permission_name.trim(),
          description: form.description.trim() || null
        };
        if (!isSystem) {
          body.module_name = form.module_name.trim() || null;
          body.action_name = form.action_name.trim() || null;
          body.status = form.status;
        }
        const res = await platformApiFetch(`/api/platform/permissions/${id}`, {
          method: 'PATCH',
          body
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Save failed');
        navigate(`/platform/permissions/${id}`);
      } else {
        const res = await platformApiFetch('/api/platform/permissions', {
          method: 'POST',
          body: {
            permission_code: form.permission_code.trim().toLowerCase(),
            permission_name: form.permission_name.trim(),
            module_name: form.module_name.trim() || null,
            action_name: form.action_name.trim() || null,
            description: form.description.trim() || null,
            status: form.status
          }
        });
        const json = await res.json().catch(() => ({}));
        if (!res.ok) throw new Error(json.error || 'Create failed');
        navigate(`/platform/permissions/${json.permission.id}`);
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        {isEdit ? 'Edit permission' : 'New permission'}
      </Typography>
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}
      <Paper component="form" onSubmit={submit} sx={{ p: 3, maxWidth: 560 }}>
        {!isEdit && (
          <TextField
            label="Permission code"
            required
            fullWidth
            margin="normal"
            value={form.permission_code}
            onChange={(e) => setForm((f) => ({ ...f, permission_code: e.target.value }))}
            helperText="Lowercase, dots allowed, e.g. tenant.reports.export"
          />
        )}
        {isEdit && (
          <TextField
            label="Permission code"
            fullWidth
            margin="normal"
            value={form.permission_code}
            disabled
            helperText="Code cannot be changed"
          />
        )}
        <TextField
          label="Permission name"
          required
          fullWidth
          margin="normal"
          value={form.permission_name}
          onChange={(e) => setForm((f) => ({ ...f, permission_name: e.target.value }))}
        />
        <TextField
          label="Module name"
          fullWidth
          margin="normal"
          value={form.module_name}
          onChange={(e) => setForm((f) => ({ ...f, module_name: e.target.value }))}
          disabled={isSystem}
          helperText={isSystem ? 'Derived for system permissions' : 'e.g. tenant.users'}
        />
        <TextField
          label="Action name"
          fullWidth
          margin="normal"
          value={form.action_name}
          onChange={(e) => setForm((f) => ({ ...f, action_name: e.target.value }))}
          disabled={isSystem}
          helperText={isSystem ? 'Derived for system permissions' : 'e.g. view'}
        />
        <TextField
          label="Description"
          fullWidth
          margin="normal"
          multiline
          minRows={2}
          value={form.description}
          onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
        />
        {!isSystem && (
          <FormControl fullWidth margin="normal">
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => setForm((f) => ({ ...f, status: e.target.value }))}
            >
              {STATUSES.map((s) => (
                <MenuItem key={s} value={s}>
                  {s}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        )}
        <Box sx={{ mt: 2, display: 'flex', gap: 1 }}>
          <Button type="submit" variant="contained" disabled={saving}>
            {saving ? <CircularProgress size={22} /> : 'Save'}
          </Button>
          <Button onClick={() => navigate(isEdit ? `/platform/permissions/${id}` : '/platform/permissions')}>
            Cancel
          </Button>
        </Box>
      </Paper>
    </Box>
  );
}
